import React from 'react';
import { Box, Button, Chip, Divider, Grid2 as Grid, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
    Add,
    ArrowBack,
    MonetizationOnOutlined,
    OpenInNew,
    PersonOutline,
    Refresh,
    Schedule,
    Search,
    SpaOutlined,
    WarningAmber,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import ContentViewComponent from '../../../../Component/ContentViewComponent/ContentViewComponent';
import ApiHandler from '../../../../Handler/ApiHandler';
import { getRoutePathname } from '../../../../Handler/RouteHandler';
import { dispatch } from '../../../../App';
import { SnackbarOpen } from '../../../../Reducer/SnackBarReducer/SnackBarSlice';
import { formatDuration } from '../../../../Common/DurationCommon';

const STATUS_LABEL_ALL = 'Tous';
const STATUS_OPTIONS = [STATUS_LABEL_ALL, 'Non confirmé', 'Confirmé', 'Terminé', 'Annulé'];
const APP_COLORS = {
    text: 'var(--greytext)',
    border: 'var(--customer_brown_light)',
    borderStrong: 'var(--customer_brown)',
    bgSoft: 'var(--customer_grey_light)',
    bgTheme: 'var(--themegreen)',
    accent: 'var(--thememarron)',
    actionPrimary: 'var(--customer_red)',
    actionPrimaryDark: 'var(--customer_red_dark)',
};

const getStatusColor = (status) => {
    if (status === 'Non confirmé') {
        return { background: '#fff8e1', text: '#8a5a00', border: '#f2d38d' };
    }
    if (status === 'Confirmé') {
        return { background: '#e7f3ff', text: '#0f4b89', border: '#c6ddf6' };
    }
    if (status === 'Terminé') {
        return { background: '#e8f8ef', text: '#1a6b38', border: '#c8e9d5' };
    }
    if (status === 'Annulé') {
        return { background: '#fdecec', text: '#8f1d1d', border: '#f5cece' };
    }

    return { background: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
};

const formatPrice = (price) => `${Number(price || 0).toFixed(2)} €`;

function buildMetrics(rows) {
    const sorted = [...rows].sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());
    const statuses = {
        'Non confirmé': 0,
        Confirmé: 0,
        Terminé: 0,
        Annulé: 0,
    };

    let totalRevenue = 0;
    let totalClientMinutes = 0;
    let totalPlanningMinutes = 0;
    let notesCount = 0;
    const overlaps = [];

    sorted.forEach((item, index) => {
        const status = item.status || '';
        if (Object.prototype.hasOwnProperty.call(statuses, status)) {
            statuses[status] += 1;
        }

        totalRevenue += Number(item.effectivePrice ?? item.prestationPrice ?? 0);
        totalClientMinutes += Number(item.effectiveDuration || item.prestationDuration || 0);
        totalPlanningMinutes += Number(item.effectivePlanningDuration || 0);
        if ((item.note || '').trim().length > 0) notesCount += 1;

        if (index > 0) {
            const previous = sorted[index - 1];
            if (moment(item.startAt).isBefore(moment(previous.endAt))) {
                overlaps.push({
                    first: previous,
                    second: item,
                });
            }
        }
    });

    const firstStart = sorted[0]?.startAt || null;
    const lastEnd = sorted[sorted.length - 1]?.endAt || null;

    return {
        total: sorted.length,
        statuses,
        totalRevenue,
        totalClientMinutes,
        totalPlanningMinutes,
        notesCount,
        overlaps,
        firstStart,
        lastEnd,
    };
}

function CrmAppointmentDay() {
    const [loading, setLoading] = React.useState(true);
    const [refreshing, setRefreshing] = React.useState(false);
    const [breadcrumbs, setBreadcrumbs] = React.useState({});
    const [appointments, setAppointments] = React.useState([]);
    const [statusFilter, setStatusFilter] = React.useState(STATUS_LABEL_ALL);
    const [searchText, setSearchText] = React.useState('');
    const params = useParams();
    const navigate = useNavigate();

    const isoDate = params.date;
    const isValidDate = moment(isoDate, 'YYYY-MM-DD', true).isValid();
    const isToday = isoDate === moment().format('YYYY-MM-DD');
    const dateLabel = isValidDate ? moment(isoDate, 'YYYY-MM-DD', true).format('dddd DD MMMM YYYY') : 'Date invalide';

    const loadDay = React.useCallback((isManualRefresh = false) => {
        if (!isValidDate) {
            dispatch(
                SnackbarOpen({
                    text: 'Date invalide.',
                    variant: 'error',
                }),
            );
            setLoading(false);
            setRefreshing(false);
            navigate(getRoutePathname('crm_appointment_list'));
            return;
        }

        if (isManualRefresh) {
            setRefreshing(true);
        }

        ApiHandler.get(
            {
                route: 'crm_appointment_list',
                data: {
                    limit: 5000,
                    offset: 0,
                    sortName: 'startAt',
                    sortDirection: 'asc',
                    dateFrom: isoDate,
                    dateTo: isoDate,
                },
            },
            (response) => {
                if (response.status === 200) {
                    setAppointments(response.data.data || []);
                } else {
                    dispatch(
                        SnackbarOpen({
                            text:
                                response.error && response.error.message
                                    ? response.error.message
                                    : "Une erreur inattendu s'est produite.",
                            variant: 'error',
                        }),
                    );
                }

                setLoading(false);
                setRefreshing(false);
            },
        );
    }, [isoDate, isValidDate, navigate]);

    React.useEffect(() => {
        setBreadcrumbs({
            title: 'CRM',
            context: `Détail du ${isValidDate ? moment(isoDate, 'YYYY-MM-DD', true).format('DD/MM/YYYY') : '--/--/----'}`,
            description: '',
            links: [
                { label: 'Calendrier des rendez-vous', path: getRoutePathname('crm_appointment_list') },
            ],
        });
        setLoading(true);
        setRefreshing(false);
        loadDay();
    }, [isValidDate, isoDate, loadDay]);

    const metrics = React.useMemo(() => buildMetrics(appointments), [appointments]);

    const filteredAppointments = React.useMemo(() => {
        const search = searchText.trim().toLowerCase();
        return appointments.filter((item) => {
            const matchStatus = statusFilter === STATUS_LABEL_ALL ? true : item.status === statusFilter;
            if (!matchStatus) return false;

            if (!search) return true;
            const customer = `${item.customerLastname || ''} ${item.customerFirstname || ''}`.toLowerCase();
            const prestation = String(item.prestationLabel || '').toLowerCase();
            const note = String(item.note || '').toLowerCase();
            const slot = `${moment(item.startAt).format('HH:mm')} ${moment(item.endAt).format('HH:mm')}`.toLowerCase();

            return customer.includes(search) || prestation.includes(search) || note.includes(search) || slot.includes(search);
        });
    }, [appointments, statusFilter, searchText]);

    const statusFilterCounts = React.useMemo(() => {
        const counts = {
            [STATUS_LABEL_ALL]: appointments.length,
        };
        STATUS_OPTIONS.slice(1).forEach((value) => {
            counts[value] = appointments.filter((item) => item.status === value).length;
        });
        return counts;
    }, [appointments]);

    return (
        <ContentViewComponent loading={loading} breadcrumbs={breadcrumbs}>
            <Box
                sx={{
                    border: `1px solid ${APP_COLORS.border}`,
                    borderRadius: '14px',
                    p: { xs: 1.2, md: 1.6 },
                    mb: 1.2,
                    background: `linear-gradient(110deg, ${APP_COLORS.bgTheme} 0%, #ffffff 52%, ${APP_COLORS.accent} 100%)`,
                    boxShadow: '0 3px 14px rgba(15, 23, 42, 0.05)',
                }}
            >
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', lg: 'center' }}
                    spacing={1}
                >
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: APP_COLORS.text, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            Journee de travail
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: '#3b2f2b', lineHeight: 1.2 }}>
                            {dateLabel}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: APP_COLORS.text, mt: 0.3 }}>
                            {isToday ? 'Aujourd hui' : 'Date selectionnee'} · {filteredAppointments.length} sur {appointments.length} rendez-vous affiches
                        </Typography>
                    </Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0.8}
                        flexWrap="wrap"
                        sx={{ width: { xs: '100%', lg: 'auto' } }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(getRoutePathname('crm_appointment_list'))}
                            aria-label="Retour planning"
                            sx={{
                                borderRadius: '10px',
                                width: { xs: '100%', sm: 'auto' },
                                borderColor: APP_COLORS.borderStrong,
                                color: APP_COLORS.text,
                                backgroundColor: '#fff',
                            }}
                        >
                            Retour planning
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={() => loadDay(true)}
                            disabled={refreshing}
                            sx={{
                                borderRadius: '10px',
                                width: { xs: '100%', sm: 'auto' },
                                borderColor: APP_COLORS.borderStrong,
                                color: APP_COLORS.text,
                                backgroundColor: '#fff',
                            }}
                        >
                            {refreshing ? 'Actualisation...' : 'Actualiser'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate(`${getRoutePathname('crm_appointment', { id: 'add' })}?date=${isoDate}`)}
                            aria-label="Ajouter rendez-vous"
                            sx={{
                                borderRadius: '10px',
                                width: { xs: '100%', sm: 'auto' },
                                backgroundColor: APP_COLORS.actionPrimary,
                                '&:hover': { backgroundColor: APP_COLORS.actionPrimaryDark },
                            }}
                        >
                            Ajouter rendez-vous
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <Box
                sx={{
                    mb: 1.2,
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(4, minmax(0, 1fr))',
                    },
                }}
            >
                <Box sx={{ border: `1px solid ${APP_COLORS.border}`, borderRadius: '12px', p: 1.1, backgroundColor: '#fff' }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text, fontWeight: 700 }}>Rendez-vous</Typography>
                    <Typography sx={{ fontSize: 23, fontWeight: 800, color: '#0f172a' }}>{metrics.total}</Typography>
                </Box>
                <Box sx={{ border: `1px solid ${APP_COLORS.border}`, borderRadius: '12px', p: 1.1, backgroundColor: '#fff' }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text, fontWeight: 700 }}>CA estime</Typography>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#0f5132', mt: 0.3 }}>
                        {formatPrice(metrics.totalRevenue)}
                    </Typography>
                </Box>
                <Box sx={{ border: `1px solid ${APP_COLORS.border}`, borderRadius: '12px', p: 1.1, backgroundColor: '#fff' }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text, fontWeight: 700 }}>Charge planning</Typography>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#1f3b5a', mt: 0.3 }}>
                        {formatDuration(metrics.totalPlanningMinutes)}
                    </Typography>
                </Box>
                <Box sx={{ border: `1px solid ${APP_COLORS.border}`, borderRadius: '12px', p: 1.1, backgroundColor: '#fff' }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text, fontWeight: 700 }}>Amplitude</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mt: 0.6 }}>
                        {metrics.firstStart ? moment(metrics.firstStart).format('HH:mm') : '--:--'} - {metrics.lastEnd ? moment(metrics.lastEnd).format('HH:mm') : '--:--'}
                    </Typography>
                </Box>
            </Box>

            {!!appointments.length && (
                <Box sx={{ mb: 1.2, px: 0.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ color: APP_COLORS.text, fontSize: 13, fontWeight: 700 }}>
                        {appointments.length} rendez-vous sur la journée
                    </Typography>
                    <Typography sx={{ color: APP_COLORS.text, fontSize: 12 }}>
                        Notes: {metrics.notesCount} · Temps client: {formatDuration(metrics.totalClientMinutes)}
                    </Typography>
                </Box>
            )}

            <Box sx={{ border: `1px solid ${APP_COLORS.border}`, borderRadius: '12px', p: 1.1, mb: 1.3, backgroundColor: '#fff' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Rechercher client, prestation, note ou horaire"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        sx={{
                            width: { xs: '100%', md: '50%' },
                            '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Stack
                        direction="row"
                        spacing={0.7}
                        sx={{
                            width: { xs: '100%', md: '50%' },
                            overflowX: 'auto',
                            pb: 0.2,
                            flexWrap: 'nowrap',
                        }}
                    >
                        {STATUS_OPTIONS.map((status) => {
                            const selected = statusFilter === status;
                            const colors = getStatusColor(status === STATUS_LABEL_ALL ? '' : status);
                            return (
                                <Chip
                                    key={status}
                                    label={`${status} (${statusFilterCounts[status] || 0})`}
                                    clickable
                                    onClick={() => setStatusFilter(status)}
                                    sx={{
                                        fontWeight: 700,
                                        borderRadius: '10px',
                                        border: selected ? `1px solid ${colors.border}` : '1px solid #e5e7eb',
                                        backgroundColor: selected ? colors.background : APP_COLORS.bgSoft,
                                        color: selected ? colors.text : APP_COLORS.text,
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Stack>
            </Box>

            {metrics.overlaps.length > 0 && (
                <Box
                    sx={{
                        mb: 1.2,
                        p: 1.1,
                        border: '1px solid #f2d3a0',
                        borderRadius: '12px',
                        backgroundColor: '#fff8eb',
                        color: '#7a4600',
                    }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <WarningAmber sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            Attention: {metrics.overlaps.length} chevauchement(s) detecte(s) dans la journee.
                        </Typography>
                    </Stack>
                </Box>
            )}

            {!appointments.length && (
                <Box sx={{ border: '1px solid #ececec', borderRadius: '10px', p: 2, backgroundColor: '#fff' }}>
                    <Typography sx={{ color: '#666', fontSize: 14 }}>
                        Aucun rendez-vous sur cette journée.
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => navigate(`${getRoutePathname('crm_appointment', { id: 'add' })}?date=${isoDate}`)}
                        sx={{ mt: 1.2 }}
                    >
                        Ajouter un rendez-vous
                    </Button>
                </Box>
            )}

            {!!appointments.length && !filteredAppointments.length && (
                <Box sx={{ border: '1px solid #ececec', borderRadius: '10px', p: 2, backgroundColor: '#fff' }}>
                    <Typography sx={{ color: '#666', fontSize: 14 }}>
                        Aucun rendez-vous ne correspond aux filtres.
                    </Typography>
                </Box>
            )}

            {filteredAppointments.map((item) => {
                const prestationDuration = Number(item.prestationDuration || 0);
                const extraDuration = Number(item.planningDuration ?? item.prestationPlanningDuration ?? 0);
                const effectivePlanningDuration = Number(item.effectivePlanningDuration || 0);
                const effectiveDuration = Number(item.effectiveDuration || prestationDuration || 0);
                const effectivePrice = Number(item.effectivePrice ?? item.prestationPrice ?? 0);
                const complements = Array.isArray(item.complements) ? item.complements : [];
                const statusColors = getStatusColor(item.status);
                const customerLabel = `${item.customerLastname ?? ''} ${item.customerFirstname ?? ''}`.trim() || 'Client non renseigne';

                return (
                    <Box
                        key={item.id}
                        sx={{
                            border: `1px solid ${APP_COLORS.border}`,
                            borderRadius: '14px',
                            p: { xs: 1.1, md: 1.3 },
                            mb: 1,
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(26,44,73,0.05)',
                            '&:hover': { borderColor: APP_COLORS.borderStrong, boxShadow: '0 5px 16px rgba(26,44,73,0.10)' },
                        }}
                    >
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={1}>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f2f4f', mb: 0.2 }}>
                                    {moment(item.startAt).format('HH:mm')} - {moment(item.endAt).format('HH:mm')}
                                </Typography>
                                <Typography sx={{ color: APP_COLORS.text, fontSize: 12 }}>
                                    Rendez-vous #{item.id}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={item.status || 'Non defini'}
                                    sx={{
                                        fontWeight: 700,
                                        border: `1px solid ${statusColors.border}`,
                                        backgroundColor: statusColors.background,
                                        color: statusColors.text,
                                    }}
                                />
                                <Chip
                                    size="small"
                                    icon={<MonetizationOnOutlined sx={{ fontSize: 14 }} />}
                                    label={formatPrice(effectivePrice)}
                                    sx={{ fontWeight: 700, backgroundColor: '#edf7ed', color: '#1a6b38' }}
                                />
                            </Stack>
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.6 }}>
                                    <PersonOutline sx={{ fontSize: 18, color: '#3f4f62' }} />
                                    <Typography sx={{ color: '#1f2937', fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                                        {customerLabel}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.9 }}>
                                    <SpaOutlined sx={{ fontSize: 18, color: '#3f4f62' }} />
                                    <Typography sx={{ color: '#1f2937', fontSize: 13, fontWeight: 600 }}>
                                        {item.prestationLabel ?? 'Prestation non renseignee'}
                                    </Typography>
                                </Stack>
                                {!!complements.length && (
                                    <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6}>
                                        {complements.map((complement) => (
                                            <Chip
                                                key={`${item.id}-complement-${complement.id}`}
                                                size="small"
                                                label={`${complement.label} (+${formatDuration(Number(complement.duration || 0))})`}
                                                sx={{ fontSize: 11, backgroundColor: APP_COLORS.bgSoft, color: '#41556d' }}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Stack spacing={0.4}>
                                    <Typography sx={{ color: '#5f6f82', fontSize: 12 }}>
                                        Duree client: {effectiveDuration > 0 ? formatDuration(effectiveDuration) : '-'}
                                    </Typography>
                                    <Typography sx={{ color: '#5f6f82', fontSize: 12 }}>
                                        Temps planning: {effectivePlanningDuration > 0 ? formatDuration(effectivePlanningDuration) : '-'}
                                    </Typography>
                                    <Typography sx={{ color: '#5f6f82', fontSize: 12 }}>
                                        Temps en plus principal: {formatDuration(extraDuration)}
                                    </Typography>
                                    <Typography sx={{ color: '#5f6f82', fontSize: 12 }}>
                                        Cree le: {item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY HH:mm') : '-'}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>

                        {!!(item.note || '').trim() && (
                            <Box sx={{ mt: 1, p: 0.9, borderRadius: '10px', backgroundColor: '#fff7e6', border: '1px solid #f2ddae' }}>
                                <Typography sx={{ fontSize: 12, color: '#7a4a00', fontWeight: 700, mb: 0.2 }}>
                                    Note interne
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: '#7a4a00' }}>
                                    {item.note}
                                </Typography>
                            </Box>
                        )}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ mt: 1 }}>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<OpenInNew />}
                                onClick={() => navigate(getRoutePathname('crm_appointment', { id: item.id }))}
                                aria-label={`Ouvrir rendez-vous ${item.id}`}
                                sx={{
                                    borderRadius: '9px',
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' },
                                    backgroundColor: APP_COLORS.actionPrimary,
                                    '&:hover': { backgroundColor: APP_COLORS.actionPrimaryDark },
                                }}
                            >
                                Ouvrir rendez-vous
                            </Button>
                            <Tooltip title={item.customerId ? 'Ouvrir la fiche client' : 'Client non renseigne'} arrow>
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PersonOutline />}
                                        disabled={!item.customerId}
                                        onClick={() => {
                                            if (item.customerId) {
                                                navigate(getRoutePathname('crm_customer', { id: item.customerId }));
                                            }
                                        }}
                                        sx={{
                                            borderRadius: '9px',
                                            textTransform: 'none',
                                            width: { xs: '100%', sm: 'auto' },
                                            borderColor: APP_COLORS.borderStrong,
                                            color: APP_COLORS.text,
                                        }}
                                    >
                                        Fiche client
                                    </Button>
                                </span>
                            </Tooltip>
                            <Chip
                                size="small"
                                icon={<Schedule sx={{ fontSize: 14 }} />}
                                label={`${moment(item.startAt).format('HH:mm')} - ${moment(item.endAt).format('HH:mm')}`}
                                sx={{
                                    ml: { xs: 0, sm: 'auto' },
                                    width: { xs: '100%', sm: 'auto' },
                                    justifyContent: { xs: 'flex-start', sm: 'center' },
                                    backgroundColor: APP_COLORS.bgSoft,
                                    color: '#374151',
                                    fontWeight: 700,
                                }}
                            />
                        </Stack>
                    </Box>
                );
            })}
        </ContentViewComponent>
    );
}

export default CrmAppointmentDay;
